import { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
} from 'recharts';
import { DollarSign, ShoppingCart, Users, TrendingUp, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { getStats, getRevenueData, getCustomerGrowthData } from '../lib/api';
import type { DashboardStats, RevenueDataPoint, CustomerGrowthDataPoint } from '../types';

export default function Dashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [revenueData, setRevenueData] = useState<RevenueDataPoint[]>([]);
  const [customerData, setCustomerData] = useState<CustomerGrowthDataPoint[]>([]);
  const [period, setPeriod] = useState('7d');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchData(): Promise<void> {
      try {
        const [statsData, revenue, customers] = await Promise.all([
          getStats(),
          getRevenueData(period),
          getCustomerGrowthData(period),
        ]);
        setStats(statsData);
        setRevenueData(revenue);
        setCustomerData(customers);
      } catch {
        setStats(null);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, [period]);

  function formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD' }).format(value);
  }

  function formatNumber(value: number): string {
    return new Intl.NumberFormat('en-SG').format(value);
  }

  const statCards = [
    {
      label: 'Total Revenue',
      value: stats ? formatCurrency(stats.totalRevenue) : '-',
      change: stats?.revenueChange ?? 0,
      icon: DollarSign,
      color: 'text-success',
      bgColor: 'bg-success/10',
    },
    {
      label: 'Total Orders',
      value: stats ? formatNumber(stats.totalOrders) : '-',
      change: stats?.ordersChange ?? 0,
      icon: ShoppingCart,
      color: 'text-primary-600',
      bgColor: 'bg-primary-50',
    },
    {
      label: 'Customers',
      value: stats ? formatNumber(stats.totalCustomers) : '-',
      change: stats?.customersChange ?? 0,
      icon: Users,
      color: 'text-secondary-600',
      bgColor: 'bg-secondary-50',
    },
    {
      label: 'Conversion Rate',
      value: stats ? `${stats.conversionRate.toFixed(2)}%` : '-',
      change: stats?.conversionChange ?? 0,
      icon: TrendingUp,
      color: 'text-warning',
      bgColor: 'bg-warning/10',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
          <p className="text-sm text-muted-foreground">Overview of your business performance</p>
        </div>
        <div className="flex gap-2">
          {['7d', '30d', '90d'].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                period === p
                  ? 'bg-primary-600 text-white'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((card) => (
          <div key={card.label} className="card">
            <div className="flex items-center justify-between">
              <div className={`rounded-lg p-2 ${card.bgColor}`}>
                <card.icon className={`h-5 w-5 ${card.color}`} />
              </div>
              <div className={`flex items-center gap-1 text-sm font-medium ${card.change >= 0 ? 'text-success' : 'text-destructive'}`}>
                {card.change >= 0 ? (
                  <ArrowUpRight className="h-4 w-4" />
                ) : (
                  <ArrowDownRight className="h-4 w-4" />
                )}
                {Math.abs(card.change).toFixed(1)}%
              </div>
            </div>
            <div className="mt-4">
              <p className="text-2xl font-bold text-foreground">{card.value}</p>
              <p className="text-sm text-muted-foreground">{card.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="card">
          <h2 className="mb-4 text-lg font-semibold text-foreground">Revenue Over Time</h2>
          {isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <p className="text-muted-foreground">Loading...</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="date" className="text-xs text-muted-foreground" />
                <YAxis className="text-xs text-muted-foreground" tickFormatter={(v: number) => `$${v}`} />
                <Tooltip
                  formatter={(value: number) => formatCurrency(value)}
                  contentClassName="bg-surface-elevated border border-border rounded-lg shadow-sm"
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#ED2939"
                  fill="oklch(0.95 0.02 15)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="card">
          <h2 className="mb-4 text-lg font-semibold text-foreground">Orders Over Time</h2>
          {isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <p className="text-muted-foreground">Loading...</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="date" className="text-xs text-muted-foreground" />
                <YAxis className="text-xs text-muted-foreground" />
                <Tooltip
                  contentClassName="bg-surface-elevated border border-border rounded-lg shadow-sm"
                />
                <Bar dataKey="orders" fill="#ED2939" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="card">
        <h2 className="mb-4 text-lg font-semibold text-foreground">Customer Growth</h2>
        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <p className="text-muted-foreground">Loading...</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={customerData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis dataKey="date" className="text-xs text-muted-foreground" />
              <YAxis className="text-xs text-muted-foreground" />
              <Tooltip
                contentClassName="bg-surface-elevated border border-border rounded-lg shadow-sm"
              />
              <Line
                type="monotone"
                dataKey="customers"
                stroke="oklch(0.55 0.1 240)"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
